import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Brain, Shield, Lock, FileCheck, Clock } from 'lucide-react';

const Intake = () => {
  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 flex h-20 items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="header-logo">
            <img
              src="https://customer-assets.emergentagent.com/job_wellness-journeys/artifacts/amcb4t0p_Generated_image%20%281%29.png"
              alt="Therapy with Ayesha"
              className="h-16 w-auto"
            />
          </Link>
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-sm font-medium hover:text-primary">Home</Link>
            <Link to="/services" className="text-sm font-medium hover:text-primary">Services</Link>
            <Link to="/resources" className="text-sm font-medium hover:text-primary">Healing Resources</Link>
            <Link to="/#contact" className="text-sm font-medium hover:text-primary">Contact</Link>
          </nav>
        </div>
      </header>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto space-y-10">
          {/* Header */}
          <div className="text-center space-y-4">
            <h1 className="text-4xl md:text-5xl font-serif font-bold text-foreground">
              Client Intake Form
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Share a little about yourself so we can prepare for a meaningful first session together.
            </p>
            <div className="flex flex-wrap gap-3 justify-center pt-2">
              <Badge variant="secondary" className="px-3 py-1.5">
                <Lock className="w-3 h-3 mr-1.5" /> Confidential
              </Badge>
              <Badge variant="secondary" className="px-3 py-1.5">
                <Shield className="w-3 h-3 mr-1.5" /> Secure
              </Badge>
              <Badge variant="secondary" className="px-3 py-1.5">
                <FileCheck className="w-3 h-3 mr-1.5" /> HIPAA-Mindful
              </Badge>
            </div>
          </div>

          {/* Information Card */}
          <Card className="border-primary/30 bg-card">
            <CardHeader>
              <CardTitle className="text-2xl font-serif">What to Expect</CardTitle>
              <CardDescription>This form takes about 5-7 minutes to complete</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <div className="w-10 h-10 rounded-full bg-secondary/40 flex items-center justify-center">
                    <FileCheck className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-semibold">Personal Details</h3>
                  <p className="text-sm text-muted-foreground">Basic contact information so I can reach you</p>
                </div>
                <div className="space-y-2">
                  <div className="w-10 h-10 rounded-full bg-secondary/40 flex items-center justify-center">
                    <Brain className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-semibold">Your Concerns</h3>
                  <p className="text-sm text-muted-foreground">What's bringing you to therapy at this time</p>
                </div>
                <div className="space-y-2">
                  <div className="w-10 h-10 rounded-full bg-secondary/40 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-semibold">Scheduling</h3>
                  <p className="text-sm text-muted-foreground">Preferred days and times for weekly sessions</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Embedded Google Form */}
          <Card className="overflow-hidden">
            <CardContent className="p-0">
              <iframe
                src="https://docs.google.com/forms/d/1WJclltaZX4in4LPxoVnRqn12MiEucZ4fMHu_U8kjI00/viewform?embedded=true"
                width="100%"
                height="1400"
                frameBorder="0"
                marginHeight="0"
                marginWidth="0"
                title="Client Intake Form"
                className="w-full"
                data-testid="intake-form-iframe"
              >
                Loading form...
              </iframe>
            </CardContent>
          </Card>

          {/* Form Setup Notice */}
          <Card className="bg-amber-50 border-amber-200 border-dashed">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
                <div className="space-y-2 text-sm">
                  <p className="font-semibold text-amber-900">Setup Note for Ayesha</p>
                  <p className="text-amber-800 leading-relaxed">
                    To display the form properly, please open your Google Form, click <strong>Send</strong>,
                    select the <strong>&lt; &gt; embed</strong> icon, and ensure the form is set to{' '}
                    <strong>"Restrict to users in your organization" = OFF</strong> (i.e., anyone with the
                    link can fill). Once published, the form will render here automatically.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Form Direct Link Fallback */}
          <Card className="bg-muted/40 border-dashed">
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground text-center">
                Having trouble viewing the form?{' '}
                <a
                  href="https://docs.google.com/forms/d/1WJclltaZX4in4LPxoVnRqn12MiEucZ4fMHu_U8kjI00/viewform"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary font-medium underline hover:text-primary/80"
                  data-testid="intake-form-direct-link"
                >
                  Open form in new tab →
                </a>
              </p>
            </CardContent>
          </Card>

          {/* Cancellation Policy Notice */}
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <div className="space-y-2">
                  <h3 className="font-semibold">Booking & Cancellation Policy</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    All sessions operate on a strict <span className="font-medium text-foreground">24-hour cancellation and advance-payment policy</span>.
                    Slots are confirmed only after fee clearance via the UPI link or QR provided at booking.
                    Cancellations made less than 24 hours before the session are non-refundable.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Intake;
